import React, { useEffect, useMemo, useRef, useState } from "react";
import { CountrySelect } from "..";
import { doGET } from "../../util/HttpUtil";
import { normalizeCountryCode } from "../../util/dialCode";
import { useTranslation } from "../../store/context/TranslationContext";
import { INPUT_AUTOFILL_PROPS } from "../InputField/autofillProps";
import PreviousValue from "../InputField/PreviousValue";
import { buildAutomationChildTestId, resolveAutomationTestId } from "../InputField/testId";
import CountrySelectService from "../CSC/CountrySelect/CountrySelectService";

const arePhoneValuesEqual = (left, right) => (
  (left?.countryCode ?? '') === (right?.countryCode ?? '')
  && (left?.phone ?? '') === (right?.phone ?? '')
);

const normalizePhoneValue = (phoneValue = {}) => ({
  ...phoneValue,
  countryCode: normalizeCountryCode(phoneValue?.countryCode),
});

export default function PhoneNumWithValidation({
  id,
  className,
  label = "Mobile Phone",
  value: initialValue,
  onChange,
  isDisabled,
  notifyParent,
  onError = () => { },
  showErr,
  inline,
  individual_id,
  required,
  borderNone,
  onClickRight,
  outline,
  inputStyle,
  group = "Individuals_Lite",
  countrySelectStyle,
  RightComponent,
  err,
  form_id,
  validateOnly = false,
  type,
  rightComponentStyle = {},
  placeholder = "Mobile Phone",
  prevValue,
  skipRemoteValidation = false,
  onCommit,
}) {
  const initialCountryCode = initialValue?.countryCode;
  const initialPhone = initialValue?.phone;
  const normalizedInitialValue = useMemo(
    () => ({
      countryCode: initialCountryCode ? String(initialCountryCode) : '',
      phone: initialPhone ?? '',
    }),
    [initialCountryCode, initialPhone],
  );
  const [value, setValue] = useState(normalizedInitialValue);
  const [error, setError] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false)
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);
  const { t } = useTranslation();
  const fieldTestId = resolveAutomationTestId(id, label);
  const bgColor = prevValue?.has ? "#FFF9D6" : isDisabled ? "#F6F6F6" : "#F1FAFC";
  const prevDisplayValue = (() => {
    if (!prevValue?.value) return "";
    if (typeof prevValue.value === "object") {
      const cc = prevValue.value.countryCode ?? "";
      const ph = prevValue.value.phone ?? "";
      return `${cc ? `+${cc}` : ""} ${ph}`.trim();
    }
    return String(prevValue.value);
  })();
  const normalizedCountryCode = normalizeCountryCode(value?.countryCode);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    setValue((prevValue) => (
      arePhoneValuesEqual(prevValue, normalizedInitialValue) ? prevValue : normalizedInitialValue
    ));
  }, [normalizedInitialValue]);

  useEffect(() => {
    setError((prevError) => (prevError === err ? prevError : err));
  }, [err]);

  useEffect(() => {
    const nextHasError = Boolean(showErr);
    setHasError((prevHasError) => (prevHasError === nextHasError ? prevHasError : nextHasError));
  }, [showErr]);

  useEffect(() => {
    const ensureCountryCode = async () => {
      if (isDisabled || valueRef.current?.countryCode) {
        return;
      }

      try {
        const countryCode = await CountrySelectService.fetchIpCallingCode();
        if (!countryCode) {
          return;
        }

        const currentValue = valueRef.current;
        if (currentValue?.countryCode) {
          return;
        }

        const nextValue = {
          countryCode: String(countryCode),
          phone: currentValue?.phone ?? '',
        };

        setValue((prev) => (
          arePhoneValuesEqual(prev, nextValue) ? prev : nextValue
        ));
        onChangeRef.current?.(nextValue);
      } catch (error) {
      }
    };

    ensureCountryCode();
  }, [isDisabled, value?.countryCode, value?.phone]);


  const handleChange = async (newData) => {
    const normalizedNewData = newData?.countryCode !== undefined
      ? { ...newData, countryCode: normalizeCountryCode(newData?.countryCode) }
      : newData;

    if (normalizedNewData?.countryCode && normalizedNewData?.countryCode != normalizedCountryCode) {
      await validatePhoneNumber({ newDataCountryCode: normalizedNewData?.countryCode, focused: isFocused });
    }
    const updatedValue = normalizePhoneValue({ ...value, ...normalizedNewData });
    setValue(updatedValue);
    onChange(updatedValue);
  };

  const validatePhoneNumber = async ({ newDataCountryCode, focused }) => {
    const effectiveCountryCode = normalizeCountryCode(newDataCountryCode ?? normalizedCountryCode);

    if (loading) return
    if (!value.phone || focused) {
      if (!value.phone) {
        if (required) {
          setError(`Please enter valid ${type == "landLine" ? "Landline" : "Mobile Phone"}`)
          onError({ errorMsg: `Please enter valid ${type == "landLine" ? "Landline" : "Mobile Phone"}`, hasError: false })
        } else {
          setError(null)
          onError({ errorMsg: "", hasError: false })
        }

      }
      return;
    }

    if (!effectiveCountryCode) return

    if (skipRemoteValidation) {
      setError(null);
      setHasError(false);
      notifyParent(true);
      onError({ errorMsg: "", hasError: false });
      return;
    }

    try {
      setLoading(true);
      const response = await doGET(`/api/${validateOnly ? "cyclos/validate/field" : "cyclos/verify/field"}?group=${group}&value=${value.phone}&field=${type == "landLine" ? "landLinePhone" : "mobilePhone"}&mobileCountryCode=${effectiveCountryCode}&form_id=${form_id}${individual_id ? `&individual_id=${individual_id}` : ""}`);
      if (response.status === 204) {
        notifyParent(true);
        onError({ errorMsg: "", hasError: false })
        setError(null);
        setHasError(false);
      } else if (response.status === 200 && response.data) {
        setError(response.data);
        onError({ errorMsg: response.data, hasError: true })
        setHasError(true);
        notifyParent(false);
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false);
    }

  };

  useEffect(() => {
    validatePhoneNumber({ focused: isFocused })
  }, [normalizedCountryCode, value?.phone, validateOnly])


  return (
    <div className={`${className} ${error ? 'error' : ''} mt-2`} data-testid={fieldTestId}>
      {outline ??
        <div style={{ minWidth: "120px ", fontSize: 16, margin: "0px 0px 5px 5px", fontWeight: "500", color: "#495057" }}>
          {t(label)}{required && <font color="red">*</font>}
        </div>
      }
      <div
        style={{
          margin: "0px 0px 5px 0px",
          height: "36px",
          backgroundColor: prevValue?.has ? "#FFF9D6" : "",
          borderRadius: "15px"
        }}
        className={`input-group flex-1 ${prevValue?.has ? "is-changed" : ""}`}
      >
        <div className="input-group-prepend">
          <CountrySelect
            value={normalizedCountryCode}
            onChange={(v) => handleChange({ countryCode: v.value })}
            onBlur={() => onCommit?.(normalizePhoneValue(value))}
            disabled={isDisabled}
            innerContainerStyle={{ borderRadius: "15px 0px 0px 15px", }}
            outline
            style={{ borderRadius: "15px 0px 0px 15px", ...countrySelectStyle }}
            className={`m-0 `}
            noLabel
            inputGroup
            showErr={showErr}
            required={required}
            // error={error}
            inline={inline}
            borderNone={borderNone}
            isDisabled={isDisabled}
            isd={true}
            prevValue={prevValue}
            suppressPreviousValue
          />
        </div>
        <input
          data-testid={buildAutomationChildTestId(fieldTestId, "input")}
          {...INPUT_AUTOFILL_PROPS}
          style={{
            height: "50px",
            borderRadius: "0px 15px 15px 0px",
            paddingLeft: "5px",
            borderTop: "none",
            borderBottom: "none",
            borderRight: "none",
            borderTop: "none",
            fontSize: "14px",
            outline: "none",
            ...inputStyle,
            backgroundColor: bgColor,
            cursor: isDisabled ? "not-allowed" : "default",
          }}
          type="number"
          className="form-control"
          placeholder={placeholder}
          value={value.phone}
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d*$/.test(value)) {
              handleChange({ phone: value });
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "e" || e.key === "E" || e.key === "+" || e.key === "-") {
              e.preventDefault();
            }
          }}

          onBlur={() => {
            setIsFocused(false)
            validatePhoneNumber({ focused: false })
            onCommit?.(normalizePhoneValue(value))
          }}
          onFocus={() => {
            setIsFocused(true)
          }}
          disabled={isDisabled}
          required
        />

      </div>
      {RightComponent && (
        <div
          data-testid={buildAutomationChildTestId(fieldTestId, "right")}
          onClick={(e) => (onClickRight ? onClickRight(e) : null)}
          style={{ height: "32px", marginRight: '0px', ...rightComponentStyle }}
          className={onClickRight ? "component_pointer inputfield__rightComponent" : "inputfield__rightComponent "}
        >
          {RightComponent()}
        </div>
      )}
      {(hasError && !loading && error && !isDisabled) && <div className="error-message mt-3" data-testid={buildAutomationChildTestId(fieldTestId, "error")}> <em className="selectfield__error">{t(error)}</em></div>}
      {loading ? <div className="selectfield__info mt-3" data-testid={buildAutomationChildTestId(fieldTestId, "loading")}>{t("Validating Mobile Phone...")}</div> : null}

      <div className="selectfield__info mt-3">
        <PreviousValue
          show={prevValue?.has}
          value={prevDisplayValue}
          testId={buildAutomationChildTestId(fieldTestId, "previous")}
        />
      </div>


    </div>
  );
}
